import { RadioGroupProps, SelectProps, StatusIndicatorProps } from '@amzn/awsui-components-react-v3';

import {
  DocumentEntry,
  QuoteEntry,
  RFXQuotesFileProcessStatus,
  RFXQuotesFileUploadStatus,
  RFXQuoteTemplateGenerationProcessStatus,
  StringMap,
} from '../interfaces';
import { RFXDocumentType } from '../interfaces/RFXBackendModel';
import { RFXResponseState } from '../interfaces/RFXResponseForm';
import businessUnitToCategory from '../resources/businessUnitToCategoryMapping.json';
import clusterToRegionMapping from '../resources/clusterToRegionMapping.json';
import pacClusterToRegionMapping from '../resources/pacClusterToRegionMapping.json';
import regionToCurrencyMapping from '../resources/regionToCurrencyMapping.json';
import { DCDIVEST, DCSP, DCSP_LLE, IPS, PAC } from './BusinessConstants';

/**
 * Builds a SelectProps.Option object from a given string.
 * Only used in default onChange update.
 * Once onChange is deprecated, this method can be deprecated as well.
 *
 * @param option - The label for the select option.
 * @returns A SelectProps.Option object with a fixed value of '1' and the provided label.
 *
 * @example
 * const selectOption = buildSelectOption('My Option');
 * // Returns: { value: '1', label: 'My Option' }
 */
export const buildSelectOption = (option: string): SelectProps.Option => {
  return {
    value: '1',
    label: option
  } as SelectProps.Option;
}

export function buildSelectOptions(options: string[]): SelectProps.Option[] {
  let result: SelectProps.Option[] = [];
  for (let i = 0; i < options.length; ++i) {
    result.push({
      value: i + 1 + '',
      label: options[i]
    });
  }
  return result;
}

/**
 * Builds an array of options for a select component based on the provided business ID.
 *
 * @param {string|null} businessId - The ID of the business for which to build the workflow options.
 * @returns {SelectProps.Option[]} An array of options for the select component.
 */
export function buildWorkflowSelectOptions(businessId: string | null): SelectProps.Option[] {
  const result: SelectProps.Option[] = [];
  if (!businessId || !WORKFLOW_TYPES[businessId]) {
    return result;
  }
  const workflows: string[] = WORKFLOW_TYPES[businessId];

  return buildSelectOptions(workflows);
}

export function buildRegionClusterSelectOptions(regions: string[] | null,
  stringifiedClusterRegionMapping?: string | null,
  addRegion: boolean = true): SelectProps.OptionGroup[] {
  const clusterRegionMap: StringMap = JSON.parse(stringifiedClusterRegionMapping!);

  if (!regions) {
    // using set for deduping purpose
    regions = [...new Set(Object.values(clusterRegionMap))]
  }

  // make US and EU as the first two regions
  let optionIndex: number = 1;
  const optionsUS = getClusterTypes('US', clusterRegionMap, optionIndex, addRegion);
  optionIndex += optionsUS.length;
  const optionsEU = getClusterTypes('EU', clusterRegionMap, optionIndex, addRegion);
  optionIndex += optionsEU.length;
  let result: SelectProps.OptionGroup[] = [
    {
      label: 'US',
      options: optionsUS
    },
    {
      label: 'EU',
      options: optionsEU
    }
  ];
  let customRegionSet = new Set();

  customRegionSet.add('US');
  customRegionSet.add('EU');

  for (let i = 0; i < regions!.length; i++) {
    if (!customRegionSet.has(regions![i])) {
      const options = getClusterTypes(regions![i], clusterRegionMap, optionIndex, addRegion);
      optionIndex += options.length;
      result.push({
        label: regions![i],
        options: options
      });
    }
  }
  return result;
}

/**
 * Constant that represents RFX types available in radio group component.
 */
export const RFX_TYPES: RadioGroupProps.RadioButtonDefinition[] = [
  {
    value: 'RFQ',
    label: 'RFQ'
  }
];

/**
 * Constant that represents regions available in select component.
 */
export const REGION_TYPES: SelectProps.OptionGroup[] =
  buildRegionClusterSelectOptions(Object.keys(regionToCurrencyMapping), null);

/**
 * Constant that represents regions available in select component.
 */
export const PAC_REGION_TYPES: SelectProps.OptionGroup[] =
buildRegionClusterSelectOptions(null, JSON.stringify(pacClusterToRegionMapping), false);

/**
 * Constant that represents EAM template type in select component.
 */
export const IPS_QUOTE_TEMPLATE_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'EAM RFQ Template'
  },
  {
    value: '2',
    label: 'EAM Detail Template'
  },
  {
    value: '3',
    label: 'Non-EAM Template'
  },
  {
    value: '4',
    label: 'Security Guard Template'
  },
  {
    value: '5',
    label: 'Default Template'
  }
];

export const DCSP_QUOTE_TEMPLATE_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'Global Bid Template'
  },
  {
    value: '2',
    label: 'Global DE Bid Template'
  },
  {
    value: '3',
    label: 'Global GC Bid Template'
  },
  {
    value: '4',
    label: 'Global Controls Bid Template'
  },
  {
    value: '5',
    label: 'Global Commissioning Bid Template'
  }
];

/**
 * Constant that represents organizations available in select component.
 */
export const ORGANIZATION_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'AWS'
  },
  {
    value: '2',
    label: 'Retail'
  }
];

/**
 * Constant that represents business unit available in select component.
 */
export const All_BUSINESS_UNIT_TYPES: StringMap = {
  PAC: 'Power And Cooling (PAC)',
  DCDIVEST: 'DC Divest',
  IPS: 'InfraOps Planning and Sourcing (IPS)'
}

export function buildBusinessUnitTypeOptions(businessIdList: string[]) {
  const businessUnitTypes: string[] = [];

  businessIdList.forEach((businessId) => {
    businessUnitTypes.push(All_BUSINESS_UNIT_TYPES[businessId]);
  })

  return buildSelectOptions(businessUnitTypes);
}

/**
 * Constant that represents user group.
 */
export const BUSINESS_UNIT_TYPES: SelectProps.Option[] = [
  {
    value: PAC,
    label: 'Power And Cooling (PAC)'
  },
  {
    value: DCDIVEST,
    label: 'DC Divest'
  },
  {
    value: IPS,
    label: 'InfraOps Planning and Sourcing (IPS)'
  },
  {
    value: DCSP,
    label: 'Data Center Services Procurement'
  }
];

/**
 * Constant that represents workflow per BusinessId.
 */
export const WORKFLOW_TYPES: { [businessId: string]: string[] } = {
  PAC: [PAC],
  DCDIVEST: [DCDIVEST],
  IPS: [IPS],
  DCSP: [DCSP,  DCSP_LLE]
}

/**
 * Constant that represents departments available in select component.
 */
export const DEPARTMENT_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'ISCAP'
  }
];

/**
 * Constant that represents categories available in select component.
 */
export const CATEGORY_TYPES: SelectProps.Option[] = buildSelectOptions(businessUnitToCategory['PAC']);

/**
 * Constant that represents projects available in select component.
 */
export const PROJECT_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'WMW'
  },
  {
    value: '2',
    label: 'Outpost'
  },
  {
    value: '3',
    label: 'Buy Sell'
  },
  {
    value: '4',
    label: 'Traditional AWS'
  }
];

/**
 * Constant that represents currencies available in select component.
 * Must follow ISO 4217 currency code https://en.wikipedia.org/wiki/ISO_4217
 */
export const CURRENCY_TYPES: SelectProps.Option[] = [
  {
    value: '1',
    label: 'USD'
  },
  {
    value: '2',
    label: 'EUR'
  }
];

/**
 * Derive all currency value from regionToCurrencyMapping
 */
export function getCurrencyTypes(): SelectProps.Option[] {
  let customCurrencySet = new Set();
  let i = 3;

  // make USD and EUR as the first two currencies
  let result: SelectProps.Option[] = [
    {
      value: '1',
      label: 'USD'
    },
    {
      value: '2',
      label: 'EUR'
    }
  ];

  customCurrencySet.add('USD');
  customCurrencySet.add('EUR');

  for (let region in regionToCurrencyMapping) {
    let currency = regionToCurrencyMapping[region as keyof typeof regionToCurrencyMapping];
    if (!customCurrencySet.has(currency)) {
      result.push({
        value: i + '',
        label: currency
      });
      i++;
    }
    customCurrencySet.add(currency);
  }
  return result;
}

/**
 * Derive the clusters based on the selected region from clusterToRegionMapping
 */
export function getClusterTypes(
  region: string | undefined,
  clusterToRegionMappingParam: any,
  index: number,
  addRegion: boolean):
  SelectProps.Option[] {
  if (!clusterToRegionMappingParam) {
    clusterToRegionMappingParam = clusterToRegionMapping;
  }
  let result: SelectProps.Option[] = [];

  // make region itself as a default selectable
  if (addRegion) {
    result.push({
      value: index.toString(),
      label: region
    });
  }

  for (let cluster in clusterToRegionMappingParam) {

    if (addRegion && cluster === region) {
      continue;
    }

    if (clusterToRegionMappingParam[cluster as keyof typeof
      clusterToRegionMappingParam] === region) {
      result.push({
        value: (++index).toString(),
        label: cluster
      });
    }
  }
  return result;
}

/**
 * Constant that represents document types available in select component.
 */
export const DOCUMENT_TYPES: SelectProps.Option[] = [
  {
    value: RFXDocumentType.MISC,
    label: 'Miscellaneous/Others'
  },
  {
    value: RFXDocumentType.DRAWING,
    label: 'Drawing'
  },
  {
    value: RFXDocumentType.PLAN,
    label: 'Plan'
  },
  {
    value: RFXDocumentType.SPEC,
    label: 'Specification'
  },
  {
    value: RFXDocumentType.TERMS,
    label: 'Terms'
  },
  {
    value: RFXDocumentType.BID,
    label: 'Bid Package'
  }
];

export const getQuoteFileUploadStatusIndicator = (
  fileUploadStatus: RFXQuotesFileUploadStatus
): StatusIndicatorProps.Type => {
  switch (fileUploadStatus) {
    case RFXQuotesFileUploadStatus.CREATED:
      return 'info';
    case RFXQuotesFileUploadStatus.UPLOADED:
      return 'success';
    case RFXQuotesFileUploadStatus.UPDATED:
      return 'success';
    case RFXQuotesFileUploadStatus.ERROR:
      return 'error';
    case RFXQuotesFileUploadStatus.TOMBSTONED:
      return 'error';
    default:
      const _exhaustiveCheck: never = fileUploadStatus;
      return _exhaustiveCheck;
  }
};

export const getQuoteFileProcessStatusIndicator = (
  fileProcessStatus: RFXQuotesFileProcessStatus
): StatusIndicatorProps.Type => {
  switch (fileProcessStatus) {
    case RFXQuotesFileProcessStatus.NODATA:
      return 'pending';
    case RFXQuotesFileProcessStatus.SUBMITTED:
      return 'info';
    case RFXQuotesFileProcessStatus.PROCESSING:
      return 'in-progress';
    case RFXQuotesFileProcessStatus.FAILED:
      return 'error';
    case RFXQuotesFileProcessStatus.COMPLETED:
      return 'success';
    default:
      const _exhaustiveCheck: never = fileProcessStatus;
      return _exhaustiveCheck;
  }
};

export const getQuoteTemplateGenerationStatusIndicator = (
  templateGenerationStatus: RFXQuoteTemplateGenerationProcessStatus
): StatusIndicatorProps.Type => {
  switch (templateGenerationStatus) {
    case RFXQuoteTemplateGenerationProcessStatus.SUBMITTED:
      return 'info';
    case RFXQuoteTemplateGenerationProcessStatus.PROCESSING:
      return 'in-progress';
    case RFXQuoteTemplateGenerationProcessStatus.FAILED:
      return 'error';
    case RFXQuoteTemplateGenerationProcessStatus.COMPLETED:
      return 'success';
    default:
      const _exhaustiveCheck: never = templateGenerationStatus;
      return _exhaustiveCheck;
  }
};

/**
 * Constant that represents default report file name.
 */
export const DEFAULT_REPORT_FILE_NAME: string = 'Report.xlsx';

/**
 * Constant for an empty quote entry that can be assigned to initial state
 */
export const EMPTY_QUOTE_ENTRY: QuoteEntry = {
  attachmentId: '',
  uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
  processStatus: RFXQuotesFileProcessStatus.NODATA,
  version: 0,
  uploadDate: Date().toString(),
  fileName: '',
  fileURL: '',
  fileSize: '0',
  format: '',
  report: '',
  reportURL: '',
  description: '',
  stagingS3Uri: '',
  message: ''
};

/**
 * Constant that represents default quotes data.
 */
export const DEFAULT_QUOTES_DATA: QuoteEntry[] = [
  {
    attachmentId: 'GoodFileV1',
    uploadStatus: RFXQuotesFileUploadStatus.CREATED,
    version: 1,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV1.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'First file',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'FailedToProcessV1',
    uploadStatus: RFXQuotesFileUploadStatus.ERROR,
    version: 2,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV1.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'Second attempt',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'FailedToValidateV1',
    uploadStatus: RFXQuotesFileUploadStatus.ERROR,
    version: 3,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV1.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'Third attempt',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'GoodFileV2',
    uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
    version: 4,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV2.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'Fourth attempt',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'GoodFileV3',
    uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
    version: 5,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV3.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: DEFAULT_REPORT_FILE_NAME,
    reportURL: 's3://blah/blah/blah/report',
    description: 'Second file',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'GoodFileV4',
    uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
    version: 6,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV4.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: DEFAULT_REPORT_FILE_NAME,
    reportURL: 's3://blah/blah/blah/report',
    description: 'Third file',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'GoodFileV5',
    uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
    version: 7,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV5.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: DEFAULT_REPORT_FILE_NAME,
    reportURL: 's3://blah/blah/blah/report',
    description: 'Fourth file',
    processStatus: RFXQuotesFileProcessStatus.SUBMITTED,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'FailedToProcessV2',
    uploadStatus: RFXQuotesFileUploadStatus.ERROR,
    version: 8,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV5.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'Fifth file',
    processStatus: RFXQuotesFileProcessStatus.NODATA,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'FailedToValidateV2',
    uploadStatus: RFXQuotesFileUploadStatus.ERROR,
    version: 9,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV5.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: '',
    reportURL: '',
    description: 'Second attempt',
    processStatus: RFXQuotesFileProcessStatus.SUBMITTED,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  },
  {
    attachmentId: 'GoodFileV6',
    uploadStatus: RFXQuotesFileUploadStatus.UPLOADED,
    version: 10,
    uploadDate: Date().toString(),
    fileName: 'GoodFileV6.xlsx',
    fileURL: 's3://blah/blah/blah/quotes',
    fileSize: '0',
    format: 'xlsx',
    report: DEFAULT_REPORT_FILE_NAME,
    reportURL: 's3://blah/blah/blah/report',
    description: 'Third attempt',
    processStatus: RFXQuotesFileProcessStatus.SUBMITTED,
    stagingS3Uri: 'stagingS3Uri',
    message: 'message'
  }
];

/**
 * Constant that represents default attachment file name.
 */
export const DEFAULT_DOCUMENT_ATTACHMENT_NAME: string = 'attachment.docx';

/**
 * Constant that represents default document entry.
 */
export const DEFAULT_DOCUMENT_ENTRY: DocumentEntry = {
  attachmentId: '',
  type: DOCUMENT_TYPES[0],
  version: 1,
  format: 'docx',
  description: '',
  attachmentName: DEFAULT_DOCUMENT_ATTACHMENT_NAME,
  attachmentURL: 's3://blah/blah/blah/attachment',
  stagingS3Uri: 'fakeURI',
  createdDate: 'fakeDate',
  requestedBy: '',
  documentValidUntil: ''
};

/**
 * Constant that represents default document entries.
 */
export const DEFAULT_DOCUMENT_ENTRY_LIST: DocumentEntry[] = [
  {
    attachmentId: '1234',
    type: DOCUMENT_TYPES[0],
    version: 1,
    format: 'docx',
    description: '',
    attachmentName: DEFAULT_DOCUMENT_ATTACHMENT_NAME,
    attachmentURL: 's3://blah/blah/blah/attachment',
    stagingS3Uri: 's3://blah/blah/blah/attachment',
    createdDate: '04/12/2021',
    requestedBy: 'adimalik',
    documentValidUntil: '2021-04-01'
  },
  {
    attachmentId: '5678',
    type: DOCUMENT_TYPES[1],
    version: 1,
    format: 'docx',
    description: '',
    attachmentName: DEFAULT_DOCUMENT_ATTACHMENT_NAME,
    attachmentURL: 's3://blah/blah/blah/attachment',
    stagingS3Uri: 's3://blah/blah/blah/attachment',
    createdDate: '04/12/2021',
    requestedBy: 'adimalik',
    documentValidUntil: '2021-04-01'
  },
  {
    attachmentId: '9012',
    type: DOCUMENT_TYPES[2],
    version: 1,
    format: 'docx',
    description: '',
    attachmentName: DEFAULT_DOCUMENT_ATTACHMENT_NAME,
    attachmentURL: 's3://blah/blah/blah/attachment',
    stagingS3Uri: 's3://blah/blah/blah/attachment',
    createdDate: '04/12/2021',
    requestedBy: 'adimalik',
    documentValidUntil: '2021-04-01'
  }
];

/**
 * Life cycle states of RFXResponse entity after supplier has submitted their bids.
 * Used primarily for SideBySideComparision which only responses in these states
 * will be visible to compare.
 */
export const RESPONSE_POST_SUBMIT_LIFE_CYCLE_STATES: string[] = [
  RFXResponseState.SUBMITTED,
  RFXResponseState.SOFT_AWARDED,
  RFXResponseState.AWARDED,
  RFXResponseState.PARTIALLY_AWARDED,
  RFXResponseState.REJECTED,
  RFXResponseState.RECALLED
]